import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSpotsFull, imgSrc } from "../api";
import SpotMap from "../components/SpotMap";
import SpotFormModal from "../components/SpotFormModal";

function Tags({ value }) {
  if (!value) return null;
  return (
    <div className="tags">
      {String(value)
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
        .map((t) => (
          <span className="tag" key={t}>
            {t}
          </span>
        ))}
    </div>
  );
}

function SpotCard({ spot, active, onSelect, onOpen }) {
  const thumb = spot.images && spot.images[0] && spot.images[0].imgURL;
  return (
    <div
      className={"spot-card" + (active ? " active" : "")}
      onClick={() => onSelect(spot)}
      onDoubleClick={() => onOpen(spot)}
    >
      {thumb ? (
        <img className="spot-thumb" src={imgSrc(thumb)} alt={spot.name} />
      ) : (
        <div className="spot-thumb placeholder">
          {spot.name?.[0]?.toUpperCase() || "?"}
        </div>
      )}
      <div className="spot-info">
        <h3>{spot.name}</h3>
        {spot.description && <p>{spot.description}</p>}
        <Tags value={spot.tags} />
      </div>
    </div>
  );
}

export default function Home() {
  const [spots, setSpots] = useState([]);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("az"); // "az" (default) | "date"
  const [sortOpen, setSortOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editSpot, setEditSpot] = useState(null);
  const [listOpen, setListOpen] = useState(false); // mobile: bottom-sheet open?
  const navigate = useNavigate();

  // Selecting a spot flies the map there — on mobile, collapse the sheet so
  // the map (and the spot) are visible.
  const selectSpot = (spot) => {
    setSelected(spot);
    setListOpen(false);
  };

  const load = useCallback(
    (selectId) =>
      getSpotsFull()
        .then((data) => {
          const list = Array.isArray(data) ? data : [];
          setSpots(list);
          setStatus("ok");
          if (selectId != null) {
            const found = list.find((s) => s.spotID === selectId);
            if (found) setSelected(found);
          }
        })
        .catch((err) => {
          setError(err.message);
          setStatus("error");
        }),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = spots;

    // text search
    if (q) {
      list = list.filter((s) =>
        [s.name, s.description, s.tags]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    // sort
    list = [...list];
    if (sort === "date") {
      list.sort((a, b) => b.spotID - a.spotID); // newest first
    } else {
      list.sort((a, b) => (a.name || "").localeCompare(b.name || "")); // A–Z
    }
    return list;
  }, [spots, query, sort]);

  const openAdd = () => {
    setEditSpot(null);
    setShowForm(true);
  };
  return (
    <div className="layout">
      <aside className={"sidebar" + (listOpen ? " open" : "")}>
        <button
          className="sheet-handle"
          onClick={() => setListOpen((o) => !o)}
          aria-label={listOpen ? "Hide spot list" : "Show spot list"}
        >
          <span className="grip" />
          <span className="sheet-label">
            {listOpen
              ? "▾ Hide list"
              : `▴ ${status === "ok" ? filtered.length + " spots" : "Show list"}`}
          </span>
        </button>
        <div className="search-wrap">
          <div className="search">
            <svg
              className="icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              placeholder="Search skate spots"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button
              className="sort-icon"
              onClick={() => setSortOpen((o) => !o)}
              title="Sort"
              aria-label="Sort spots"
            >
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line x1="5" y1="7" x2="18" y2="7" />
                <line x1="5" y1="12" x2="13" y2="12" />
                <line x1="5" y1="17" x2="9" y2="17" />
              </svg>
            </button>
            {sortOpen && (
              <>
                <div
                  className="sort-backdrop"
                  onClick={() => setSortOpen(false)}
                />
                <div className="sort-menu">
                  <button
                    className={sort === "az" ? "on" : ""}
                    onClick={() => {
                      setSort("az");
                      setSortOpen(false);
                    }}
                  >
                    A–Z
                  </button>
                  <button
                    className={sort === "date" ? "on" : ""}
                    onClick={() => {
                      setSort("date");
                      setSortOpen(false);
                    }}
                  >
                    Date added
                  </button>
                </div>
              </>
            )}
          </div>
          <button className="add-btn" onClick={openAdd}>
            <span>＋</span> Add a spot
          </button>
          {status === "ok" && (
            <p className="count">{filtered.length} spots</p>
          )}
        </div>

        <div className="spot-list">
          {status === "loading" && (
            <div className="state">
              <div className="spinner" />
              Loading spots…
            </div>
          )}
          {status === "error" && (
            <div className="state error">
              Could not load spots: {error}
              <br />
              <small>Is the API server running?</small>
            </div>
          )}
          {status === "ok" && filtered.length === 0 && (
            <div className="state">
              {spots.length === 0
                ? "No spots yet — add one above."
                : "No spots match your search."}
            </div>
          )}
          {filtered.map((s) => (
            <SpotCard
              key={s.spotID}
              spot={s}
              active={selected?.spotID === s.spotID}
              onSelect={selectSpot}
              onOpen={(spot) => navigate(`/spots/${spot.spotID}`)}
            />
          ))}
        </div>
      </aside>

      <main className="map-pane">
        <SpotMap spots={filtered} selected={selected} />
      </main>

      {showForm && (
        <SpotFormModal
          spot={editSpot}
          onClose={() => setShowForm(false)}
          onSaved={(spotID) => {
            setShowForm(false);
            load(spotID);
          }}
        />
      )}
    </div>
  );
}
